<template>
    <div>
        <h1>PrestaShop</h1>

        <!-- ═══════════════════════════════════════════════════════════ -->
        <!-- SECTION 1 — Configuration                                   -->
        <!-- ═══════════════════════════════════════════════════════════ -->
        <div v-if="prestashop" class="section-card">
            <h3
                class="section-title section-title--toggle"
                @click="showConfig = !showConfig"
            >
                <i class="fas fa-cog"></i> Configuration
                <span class="toggle-hint">{{ showConfig ? '▲ Réduire' : '▼ Afficher' }}</span>
            </h3>

            <div v-if="showConfig">

            <b-form-group
                label="Langues du site"
                description="La première langue est la langue principale."
            >
                <array_tag_editor
                    :start_data="prestashop.languages"
                    @update="update_languages"
                />
            </b-form-group>

            <hr class="section-divider" />

            <p class="text-muted mb-3"><i class="fas fa-download"></i> Informations à <strong>récupérer depuis Prestashop</strong> (module SmartMyData → backoffice)</p>

            <b-form-group label="URL de l'API Prestashop">
                <b-input
                    v-model="prestashop.prestashop_url"
                    placeholder="http://votre-boutique.com/modules/smartmydata/api.php"
                />
            </b-form-group>

            <b-form-group label="Clé API Prestashop">
                <b-input
                    v-model="prestashop.prestashop_key"
                    placeholder="Clé générée par le module Prestashop"
                />
            </b-form-group>

            <b-form-group>
                <b-button
                    @click="testConnection"
                    variant="outline-info"
                    :disabled="!prestashop.prestashop_url || !prestashop.prestashop_key || spinner"
                >
                    <i class="fas fa-plug"></i> Tester la connexion
                </b-button>
                <div v-if="connectionStatus" class="mt-2">
                    <b-alert :variant="connectionStatus.success ? 'success' : 'danger'" show>
                        <strong>{{ connectionStatus.success ? '✓ Connexion réussie' : '✗ Problème' }}</strong>
                        <div v-if="connectionStatus.message" class="mt-1">{{ connectionStatus.message }}</div>
                        <ul v-if="connectionStatus.details" class="mt-1 mb-0">
                            <li v-for="(detail, index) in connectionStatus.details" :key="index">{{ detail }}</li>
                        </ul>
                    </b-alert>
                </div>
            </b-form-group>

            <hr class="section-divider" />

            <p class="text-muted mb-3"><i class="fas fa-upload"></i> Informations à <strong>saisir dans Prestashop</strong> (module SmartMyData → backoffice)</p>

            <b-form-group label="URL du serveur SmartMyData">
                <b-input value="https://api-prestashop.smartmydata.com" readonly class="bg-light" />
            </b-form-group>

            <b-form-group label="Clé d'authentification SmartMyData">
                <b-input-group>
                    <b-input
                        :value="prestashop.prestashop_identifiant_key"
                        readonly
                        class="bg-light"
                        placeholder="Clé d'identification unique"
                    />
                    <b-input-group-append>
                        <b-button @click="generateApiKey" variant="outline-primary" title="Générer une nouvelle clé">
                            <i class="fas fa-sync-alt"></i> Générer
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>

            <b-button variant="primary" @click="save">Sauvegarder</b-button>

            </div><!-- /showConfig -->
        </div>

        <!-- ═══════════════════════════════════════════════════════════ -->
        <!-- SECTION 2 — Synchronisation complète                       -->
        <!-- ═══════════════════════════════════════════════════════════ -->
        <div class="section-card">
            <h3
                class="section-title section-title--toggle"
                @click="showSync = !showSync"
            >
                <i class="fas fa-sync-alt"></i> Synchronisation complète
                <span class="toggle-hint">{{ showSync ? '▲ Réduire' : '▼ Afficher' }}</span>
            </h3>

            <div v-if="showSync">
            <p class="text-muted mb-3">Importe l'intégralité des données depuis PrestaShop vers SmartMyData.</p>

            <b-form-group label="Que synchroniser ?">
                <b-form-checkbox v-model="syncOptions.products">
                    <strong>Produits</strong> — tous les produits et déclinaisons
                </b-form-checkbox>
                <b-form-checkbox v-model="syncOptions.categories">
                    <strong>Catégories</strong> — toutes les catégories
                </b-form-checkbox>
                <b-form-checkbox v-model="syncOptions.orders" v-if="prestashop && prestashop.structure === 'gm'">
                    <strong>Ventes</strong> — toutes les commandes (structure GM uniquement)
                </b-form-checkbox>
            </b-form-group>

            <b-button
                @click="import_prestashop"
                variant="success"
                :disabled="(!syncOptions.products && !syncOptions.categories && !syncOptions.orders) || spinner"
            >
                <i class="fas fa-sync"></i> Synchroniser
            </b-button>

            
            </div><!-- /showSync -->
        </div>

        <!-- ═══════════════════════════════════════════════════════════ -->
        <!-- SECTION 3 — Resynchronisation ciblée (cachée par défaut)  -->
        <!-- ═══════════════════════════════════════════════════════════ -->
        <div class="section-card">
            <h3
                class="section-title section-title--toggle"
                @click="showResync = !showResync"
            >
                <i class="fas fa-crosshairs"></i> Resynchronisation ciblée par IDs
                <span class="toggle-hint">{{ showResync ? '▲ Réduire' : '▼ Afficher' }}</span>
            </h3>

            <div v-if="showResync">
                <p class="text-muted mb-3">
                    Resynchronise uniquement les enregistrements indiqués.<br />
                    Formats : <code>42</code> · <code>10,15,42</code> · <code>10-20</code> · <code>20-</code> (depuis 20) · <code>-50</code> (jusqu'à 50)
                </p>

                <b-form-group label="IDs Produits" v-if="prestashop">
                    <b-input
                        v-model="resyncIds.products"
                        placeholder="Laisser vide pour ignorer  —  ex: 42  ou  10,15,42  ou  10-20"
                    />
                </b-form-group>

                <b-form-group label="IDs Catégories" v-if="prestashop">
                    <b-input
                        v-model="resyncIds.categories"
                        placeholder="Laisser vide pour ignorer  —  ex: 5,8,12"
                    />
                </b-form-group>

                <b-form-group label="IDs Ventes" v-if="prestashop && prestashop.structure === 'gm'">
                    <b-input
                        v-model="resyncIds.orders"
                        placeholder="Laisser vide pour ignorer  —  ex: 100-200"
                    />
                </b-form-group>

                <b-button
                    @click="resync_ids"
                    variant="warning"
                    :disabled="(!resyncIds.products && !resyncIds.categories && !resyncIds.orders) || spinner"
                >
                    <i class="fas fa-crosshairs"></i> Resynchroniser
                </b-button>
            </div>
        </div>

        <div v-if="spinner" class="center_spinner">
            <fade-loader></fade-loader>
        </div>
    </div>
</template>

<script>
    import FadeLoader from "vue-spinner/src/FadeLoader.vue";
    import { v4 as uuidv4 } from 'uuid';

    import array_tag_editor from "../components/array_editor.vue";
    import treatments from "../services/treatments.js";
	import workers from "../services/workers.js";
	import client from "../services/client.js";
	import users from "../services/users.js";
    import store from "../store.js";

	export default {
		name: "prestashop",
        components: {
            FadeLoader,
            array_tag_editor
        },
		data: function () {
			return {
                spinner: false,
                prestashop: null,
                connectionStatus: null,
                showConfig: false,
                showSync: false,
                showResync: false,
                syncOptions: {
                    products: false,
                    categories: false,
                    orders: false
                },
                resyncIds: {
                    products: "",
                    categories: "",
                    orders: ""
                }
			};
		},
		methods: {
			async update_languages(data)
			{
				this.prestashop.languages = data;
			},
            generateApiKey() {
                // Génère une nouvelle clé UUID
                this.prestashop.prestashop_identifiant_key = uuidv4();

                this.save()
            },
            isAdmin: function() {
                return store.getters.isAdmin;
			},
            async testConnection() {
                await this.save();

                this.spinner = true;
                this.connectionStatus = null;

                try {
                    var formData = new FormData();
                    formData.append("action", "prestashop");
                    formData.append("parameters", JSON.stringify({
                        "action": "check_connexion",
                    }));
                    
                    var results = await workers.test(formData);

                    console.log("end test")

                     var results = await treatments.getFileAlyx(
                        results.data.files[0].url,
                        false,
                        false
                    );

                    console.log(results)
                    
                    results = this.$papa.parse(results, { header: true });
                    results = results.data[0];

                    if (results.success == "false")
                        results.success = false;
                    else
                        results.success = true;
                    if (results.details)
                        results.details = JSON.parse(results.details);

                    console.log(results)

                    console.log("Résultat du test de connexion:", results);
                    
                    if (results && results.success) {
                        this.connectionStatus = {
                            success: true,
                            message: results.message || "La connexion à PrestaShop fonctionne correctement.",
                            details: results.details
                        };
                    } else {
                        this.connectionStatus = {
                            success: false,
                            message: results.message || results.error || "Impossible de se connecter à PrestaShop.",
                            details: results.details
                        };
                    }
                } catch (error) {
                    this.connectionStatus = {
                        success: false,
                        message: "Erreur lors du test de connexion : " + error.message
                    };
                }

                this.spinner = false;
            },
            async resync_ids()
            {
                await this.save();

                this.spinner = true;

                const tasks = [];

                if (this.resyncIds.products) {
                    tasks.push(workers.test((() => {
                        var f = new FormData();
                        f.append("action", "prestashop");
                        f.append("parameters", JSON.stringify({
                            action: "resync_ids",
                            ids: this.resyncIds.products,
                            sync_products: true,
                            sync_categories: false,
                            sync_orders: false
                        }));
                        return f;
                    })()));
                }

                if (this.resyncIds.categories) {
                    tasks.push(workers.test((() => {
                        var f = new FormData();
                        f.append("action", "prestashop");
                        f.append("parameters", JSON.stringify({
                            action: "resync_ids",
                            ids: this.resyncIds.categories,
                            sync_products: false,
                            sync_categories: true,
                            sync_orders: false
                        }));
                        return f;
                    })()));
                }

                if (this.resyncIds.orders) {
                    tasks.push(workers.test((() => {
                        var f = new FormData();
                        f.append("action", "prestashop");
                        f.append("parameters", JSON.stringify({
                            action: "resync_ids",
                            ids: this.resyncIds.orders,
                            sync_products: false,
                            sync_categories: false,
                            sync_orders: true
                        }));
                        return f;
                    })()));
                }

                await Promise.all(tasks);

                this.spinner = false;

                this.$notify({
                    group: "info",
                    title: "Resynchronisation terminée",
                    type: "success",
                });
            },
            async import_prestashop()
            {
                await this.save();

                this.spinner = true;

                var formData = new FormData();
                formData.append("action", "prestashop");
                formData.append("parameters", JSON.stringify({
                    "action": "import_prestashop",
                    "sync_products": this.syncOptions.products,
                    "sync_categories": this.syncOptions.categories,
                    "sync_orders": this.syncOptions.orders
                }));
                
                var results = await workers.test(formData);

                this.spinner = false;

				this.$notify({
					group: "info",
					title: "Synchronisation terminée",
					type: "success",
				});
            },
            async save()
            {
                await client.put(null, null, null, null, null, null, null, null, null, null, null, this.prestashop);

				this.$notify({
					group: "info",
					title: "Configuration sauvegardée",
					type: "success",
				});
            }
		},
		mounted: async function () {
			
			if (store.state.user.rules && !store.state.user.rules.admin) {
				this.$router.push({ name: "profile" });
			}

            let response = await users.getSelf(true);
			var client = response.client;

            this.prestashop = client.details.prestashop;
            if (!this.prestashop)
            {
                this.prestashop = {
                    "languages": [
                        "fr"
                    ],
                    "prestashop_url": "",
                    "prestashop_key": "",
                    "structure": "",
                    "prestashop_identifiant_key": ""
                }
            }
            if (!this.prestashop.prestashop_identifiant_key)
            {            
                this.prestashop.prestashop_identifiant_key = uuidv4();
                this.save();
            }
        }
	};
</script>

<style scoped>
	.center_spinner {
		width: 0%;
		margin: 0 auto;
        margin-bottom: 60px;
	}
    .section-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 24px;
        margin-bottom: 20px;
    }
    .section-title {
        font-size: 1.15rem;
        margin-bottom: 16px;
        color: #333;
    }
    .section-title--toggle {
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 0;
    }
    .section-title--toggle:hover {
        color: #007bff;
    }
    .toggle-hint {
        margin-left: auto;
        font-size: 0.8rem;
        color: #888;
        font-weight: normal;
    }
    .section-divider {
        margin: 20px 0;
        border-color: #f0f0f0;
    }
    .section-title--toggle + div {
        margin-top: 16px;
    }
</style>